import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import axios, { isAxiosError } from 'axios';
import { VehicleShowQuery, useDeleteVehicleAttachmentMutation } from '../../../generated/graphql';
import { ConfirmModal } from '../../ConfirmModal';
import { uploadConfig } from '../../../config/upload.config';
import { authConfig } from '../../../config/auth.config';
import { useUploadFile } from '../../../hooks/use-upload-file';
import {
  MAX_ATTACHMENT_FILE_BYTES,
  MAX_VEHICLE_ATTACHMENTS,
} from 'shared-ui/lib/constants/image-upload-limits';
import { toast } from 'react-toastify';

type Attachment = VehicleShowQuery['vehicle']['attachments'][0];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
  attachments: Attachment[];
  hasActiveLicense: boolean;
  onRefetch: () => void;
};

export const VehicleAttachmentsModal = ({
  isOpen,
  onClose,
  vehicleId,
  attachments,
  hasActiveLicense,
  onRefetch,
}: Props): JSX.Element | null => {
  const { t } = useTranslation('VehicleShowScene');
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null);
  const [deleteVehicleAttachment] = useDeleteVehicleAttachmentMutation();
  const { uploadFile, isImageLoading } = useUploadFile({ type: 'vehicle-attachment' });

  const atMaxAttachments = attachments.length >= MAX_VEHICLE_ATTACHMENTS;
  const addAttachmentDisabled =
    !hasActiveLicense || isImageLoading || atMaxAttachments;

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (attachments.length >= MAX_VEHICLE_ATTACHMENTS) {
        event.target.value = '';
        return;
      }

      if (file.size > MAX_ATTACHMENT_FILE_BYTES) {
        toast.error(t('attachmentMaxSizeExceeded'));
        event.target.value = '';
        return;
      }

      await uploadFile(file, vehicleId);
      event.target.value = '';
      await onRefetch();
    },
    [attachments.length, uploadFile, vehicleId, onRefetch, t]
  );

  const handleDelete = useCallback(async () => {
    if (!attachmentToDelete) return;
    await deleteVehicleAttachment({
      variables: { id: attachmentToDelete.id },
    });
    toast.success(t('attachmentsDeleteSuccess'));
    setAttachmentToDelete(null);
    await onRefetch();
  }, [attachmentToDelete, deleteVehicleAttachment, onRefetch, t]);

  const handleOpen = useCallback(
    async (attachment: Attachment) => {
      if (!attachment.url) {
        return;
      }
      const token = localStorage.getItem(authConfig.tokenStorageKey);
      if (!token) {
        toast.error(t('attachmentsOpenNotAuthorized'));
        return;
      }

      const loadingToastId = toast.loading(t('attachmentsOpening'));
      try {
        const response = await axios.get(`${uploadConfig.api}/${attachment.url}`, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        toast.dismiss(loadingToastId);
      } catch (e) {
        toast.dismiss(loadingToastId);
        if (isAxiosError(e) && e.response && (e.response.status === 401 || e.response.status === 403)) {
          toast.error(t('attachmentsOpenForbidden'));
        } else {
          toast.error(t('attachmentsOpenError'));
        }
      }
    },
    [t]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('attachmentsTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            ✕
          </button>
        </div>
        {isImageLoading && (
          <div className="flex items-center gap-2 mb-4 py-2 px-3 bg-primary/10 border border-primary/30 rounded-md text-primary text-sm font-medium">
            <svg
              className="animate-spin h-5 w-5 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{t('attachmentsUploading')}</span>
          </div>
        )}
        <p className="text-sm text-gray-color mb-4">
          {t('attachmentsInfo')}
        </p>
        <p className="text-sm text-gray-color mb-4">
          {t('attachmentsMaxInfo')}
        </p>
        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md mb-4">
          {attachments.length === 0 ? (
            <p className="p-4 text-sm text-gray-color">{t('attachmentsEmpty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    {t('attachmentsName')}
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    {t('attachmentsCreatedAt')}
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">
                    {t('attachmentsActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attachments.map((attachment) => (
                  <tr key={attachment.id}>
                    <td className="px-4 py-2 text-gray-900 break-all">{attachment.name}</td>
                    <td className="px-4 py-2 text-gray-color">
                      {formatDate(attachment.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      {attachment.url || attachment.canDelete ? (
                        <>
                          {attachment.url ? (
                            <button
                              type="button"
                              onClick={() => handleOpen(attachment)}
                              className="inline-flex items-center px-3 py-1 border border-primary rounded-md text-xs font-medium text-primary hover:bg-secondary hover:text-white"
                            >
                              {t('attachmentsOpen')}
                            </button>
                          ) : null}
                          {attachment.canDelete ? (
                            <button
                              type="button"
                              disabled={!hasActiveLicense}
                              onClick={() => setAttachmentToDelete(attachment)}
                              className="inline-flex items-center px-3 py-1 border border-red-600 rounded-md text-xs font-medium text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50"
                            >
                              {t('attachmentsDelete')}
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex flex-col gap-2 max-w-md">
            {atMaxAttachments && (
              <p
                id="vehicle-attachments-max-reached"
                className="text-sm font-medium text-amber-800"
                role="status"
              >
                {t('attachmentsMaxReached')}
              </p>
            )}
            <label
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium bg-white ${
                addAttachmentDisabled
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed opacity-70'
                  : 'border-primary text-primary hover:bg-secondary hover:text-white cursor-pointer'
              }`}
              aria-disabled={addAttachmentDisabled}
              aria-describedby={
                atMaxAttachments ? 'vehicle-attachments-max-reached' : undefined
              }
              title={
                atMaxAttachments && !isImageLoading
                  ? t('attachmentsMaxReached')
                  : undefined
              }
            >
              {isImageLoading ? (
                <svg
                  className="animate-spin h-5 w-5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
              )}
              <span>{t('attachmentsAddNew')}</span>
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                className="hidden"
                disabled={addAttachmentDisabled}
                onChange={handleFileChange}
              />
            </label>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {t('attachmentsClose')}
          </button>
        </div>

        <ConfirmModal
          isOpen={!!attachmentToDelete}
          onCancel={() => setAttachmentToDelete(null)}
          onConfirm={handleDelete}
          title={t('attachmentsDeleteConfirmTitle')}
          description={t('attachmentsDeleteConfirmDescription')}
          confirmLabel={t('attachmentsDeleteConfirm')}
          cancelLabel={t('attachmentsDeleteCancel')}
          variant="danger"
        />
      </div>
    </div>
  );
};
